import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAuthToken, removeAuthToken, storeAuthToken, verifyToken } from '@/lib/api/auth'
import { getAuthCookie, getSessionCookie, removeAuthCookie, removeSessionCookie, setAuthCookie, setSessionCookie } from '@/lib/cookies'

// How often to re-check that the session hasn't expired while the app stays open.
const SESSION_CHECK_INTERVAL_MS = 2 * 60 * 1000

interface AuthContextType {
  isAuthenticated: boolean
  jwtToken: string | null
  loading: boolean
  login: (token: string) => void
  logout: () => Promise<void>
}

// Default context value
const defaultContextValue: AuthContextType = {
  isAuthenticated: false,
  jwtToken: null,
  loading: true,
  login: () => {},
  logout: async () => {},
}

// Create context
const AuthContext = createContext<AuthContextType>(defaultContextValue)

// Auth provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const [jwtToken, setJwtToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const jwtTokenRef = useRef<string | null>(null)

  // Check for JWT token on mount
  useEffect(() => {
    const checkToken = async () => {
      console.log('AuthProvider: Checking authentication...')

      try {
        // Get token from storage or cookie
        const token = getAuthToken() || getAuthCookie() || getSessionCookie()

        if (token) {
          console.log('AuthProvider: Token found, verifying with backend...')

          // Verify token with backend
          try {
            const isValid = await verifyToken(token)

            if (isValid) {
              console.log('AuthProvider: Token is valid')
              setJwtToken(token)
            } else {
              console.log('AuthProvider: Token is invalid or session expired, removing...')
              removeAuthToken()
              removeAuthCookie()
              removeSessionCookie()
              setJwtToken(null)
            }
          } catch (verifyError) {
            // If backend is not available, just skip verification for now
            console.warn('AuthProvider: Could not verify token with backend (backend might be offline):', verifyError)
            // Don't fail completely - just mark as not authenticated
            setJwtToken(null)
          }
        } else {
          console.log('AuthProvider: No token found')
          setJwtToken(null)
        }
      } catch (error) {
        console.error('AuthProvider: Error in checkToken:', error)
        setJwtToken(null)
      } finally {
        console.log('AuthProvider: Authentication check complete')
        setLoading(false)
      }
    }

    checkToken()
  }, [])

  // Keep a ref in sync so the polling interval below always reads the latest token
  // without needing to be torn down and recreated every time it changes.
  useEffect(() => {
    jwtTokenRef.current = jwtToken
  }, [jwtToken])

  // Login function — sets token in state, localStorage, and cookie immediately
  const login = useCallback((token: string) => {
    storeAuthToken(token)
    setAuthCookie(token)
    setSessionCookie(token)
    setJwtToken(token)
  }, [])

  // Logout function that handles JWT
  const logout = useCallback(async () => {
    try {
      removeAuthToken()
      removeAuthCookie()
      removeSessionCookie()
      setJwtToken(null)
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }, [])

  // Periodically re-verify the session while the app stays open, so an
  // expired session gets caught even if the user isn't triggering API calls.
  // A 401 from any API call is also caught globally (see the axios
  // interceptor in src/lib/api/auth.ts), which forces a redirect immediately.
  useEffect(() => {
    const interval = setInterval(async () => {
      const token = jwtTokenRef.current
      if (!token) return

      const isValid = await verifyToken(token)
      if (!isValid) {
        console.log('AuthProvider: Session expired during periodic check, logging out...')
        await logout()
        navigate('/auth')
      }
    }, SESSION_CHECK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [logout, navigate])

  // Context value
  const value: AuthContextType = {
    isAuthenticated: jwtToken !== null,
    jwtToken,
    loading,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export const useAuthContext = () => useContext(AuthContext)